import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Timer, CheckCircle2, XCircle, Circle, ArrowLeft, Loader2, Trophy, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function TestAttempt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const elapsed = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get(`/tests/${id}/take`)
      .then(({ data }) => {
        setTest(data);
        setSeconds(data.duration_minutes * 60);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Could not load test");
        navigate("/mock-tests");
      });
  }, [id, navigate]);

  const doSubmit = useCallback(async () => {
    if (submitting || result) return;
    setSubmitting(true);
    if (timerRef.current) clearInterval(timerRef.current);
    try {
      const { data } = await api.post(`/tests/${id}/submit`, { answers, time_taken: elapsed.current });
      setResult(data);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Submission failed. Please try again.");
      setSubmitting(false);
    }
  }, [answers, id, submitting, result]);

  useEffect(() => {
    if (!test || result) return;
    timerRef.current = setInterval(() => {
      elapsed.current += 1;
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          toast.info("Time's up! Submitting your test.");
          doSubmit();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [test, result, doSubmit]);

  if (!test) {
    return <div className="flex min-h-[50vh] items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-[#1F3A68] border-t-transparent" /></div>;
  }

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  const answered = Object.keys(answers).length;

  if (result) {
    const pct = result.max_marks > 0 ? Math.round((result.score / result.max_marks) * 100) : 0;
    return (
      <div className="mx-auto max-w-3xl px-4 py-8" data-testid="test-result">
        <div className="gov-card overflow-hidden">
          <div className="gov-card-header px-5 py-3 font-serif text-lg font-bold">Result — {result.test_title}</div>
          <div className="p-6">
            <div className="flex flex-col items-center">
              <Trophy size={40} className="text-[#D97706]" />
              <div className="mt-2 font-serif text-4xl font-bold text-[#0B2545]" data-testid="result-score">{result.score} <span className="text-lg text-gray-400">/ {result.max_marks}</span></div>
              <div className="text-sm text-gray-500">{pct}% score</div>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-sm bg-[#16A34A]/10 p-3"><div className="font-serif text-xl font-bold text-[#16A34A]">{result.correct}</div><div className="text-xs text-gray-600">Correct</div></div>
              <div className="rounded-sm bg-[#C4302B]/10 p-3"><div className="font-serif text-xl font-bold text-[#C4302B]">{result.wrong}</div><div className="text-xs text-gray-600">Wrong</div></div>
              <div className="rounded-sm bg-gray-100 p-3"><div className="font-serif text-xl font-bold text-gray-500">{result.unattempted}</div><div className="text-xs text-gray-600">Skipped</div></div>
            </div>
            <div className="mt-5 flex gap-2">
              <Link to="/mock-tests" className="flex-1 rounded-sm border border-[#1F3A68] py-2 text-center text-sm font-semibold text-[#1F3A68] hover:bg-[#1F3A68] hover:text-white" data-testid="result-back">All Tests</Link>
              <Link to="/dashboard" className="flex-1 rounded-sm bg-[#1F3A68] py-2 text-center text-sm font-semibold text-white hover:bg-[#0B2545]" data-testid="result-dashboard">My Dashboard</Link>
            </div>
          </div>
        </div>

        <h3 className="mt-6 font-serif text-xl font-bold text-[#0B2545]">Review & Explanations</h3>
        <div className="mt-3 space-y-4">
          {result.review.map((q, i) => (
            <div key={q.id} className="gov-card p-4" data-testid={`review-${i}`}>
              <p className="font-semibold text-[#0B2545]">{i + 1}. {q.question_text}</p>
              <ul className="mt-2 space-y-1.5">
                {q.options.map((opt, oi) => {
                  const isCorrect = oi === q.correct_index;
                  const isSelected = oi === q.selected_index;
                  return (
                    <li key={oi} className={`flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm ${isCorrect ? "bg-[#16A34A]/10 text-[#166534]" : isSelected ? "bg-[#C4302B]/10 text-[#991B1B]" : "text-gray-700"}`}>
                      {isCorrect ? <CheckCircle2 size={15} className="text-[#16A34A]" /> : isSelected ? <XCircle size={15} className="text-[#C4302B]" /> : <Circle size={14} className="text-gray-300" />}
                      {opt}
                    </li>
                  );
                })}
              </ul>
              {q.explanation && <p className="mt-2 rounded-sm bg-[#FEF3C7] p-2.5 text-xs leading-relaxed text-[#92400E]"><strong>Explanation:</strong> {q.explanation}</p>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="sticky top-14 z-30 mb-4 flex items-center justify-between rounded-sm border border-[#D1D5DB] bg-white px-4 py-3 shadow-sm">
        <Link to="/mock-tests" className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#0B2545]"><ArrowLeft size={15} /> Exit</Link>
        <span className="text-xs text-gray-500">{answered}/{test.questions.length} answered</span>
        <span className={`flex items-center gap-1.5 rounded-sm px-2.5 py-1 font-mono text-sm font-bold ${seconds < 60 ? "bg-[#C4302B]/10 text-[#C4302B]" : "bg-[#1F3A68]/10 text-[#1F3A68]"}`} data-testid="test-timer">
          <Timer size={15} /> {mm}:{ss}
        </span>
      </div>

      <h1 className="font-serif text-xl font-bold text-[#0B2545]">{test.title}</h1>
      <p className="mb-4 text-xs text-gray-500">+{test.marks_correct} for each correct answer · {test.marks_wrong} for each wrong answer</p>

      <div className="space-y-4">
        {test.questions.map((q, i) => (
          <div key={q.id} className="gov-card p-4" data-testid={`question-${i}`}>
            <p className="font-semibold text-[#0B2545]">{i + 1}. {q.question_text}</p>
            <div className="mt-3 space-y-2">
              {q.options.map((opt, oi) => (
                <label key={oi} className={`flex cursor-pointer items-center gap-2.5 rounded-sm border px-3 py-2 text-sm transition-colors ${answers[q.id] === oi ? "border-[#1F3A68] bg-[#1F3A68]/5" : "border-[#E5E7EB] hover:bg-gray-50"}`} data-testid={`q${i}-opt${oi}`}>
                  <input type="radio" name={q.id} checked={answers[q.id] === oi} onChange={() => setAnswers((a) => ({ ...a, [q.id]: oi }))} className="accent-[#1F3A68]" />
                  {opt}
                </label>
              ))}
            </div>
            {answers[q.id] !== undefined && (
              <button onClick={() => setAnswers((a) => { const n = { ...a }; delete n[q.id]; return n; })} className="mt-2 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600" data-testid={`clear-q${i}`}>
                <RotateCcw size={12} /> Clear response
              </button>
            )}
          </div>
        ))}
      </div>

      <button onClick={doSubmit} disabled={submitting} className="mt-6 flex w-full items-center justify-center gap-2 rounded-sm bg-[#FF9933] px-4 py-3 text-sm font-bold text-slate-900 hover:bg-[#E68A2E] disabled:opacity-60" data-testid="submit-test">
        {submitting && <Loader2 size={16} className="animate-spin" />} Submit Test
      </button>
    </div>
  );
}
